import { db } from '@/db/client';
import { ImageRecordSchema, type ImageRecord } from '@/schemas/image';

export interface MergeResult {
  applied: number;
  replaced: number;
}

/**
 * Compare two images for last-write-wins conflict resolution.
 * Returns true if incoming image should replace existing image.
 */
function shouldReplaceImage(
  existing: ImageRecord,
  incoming: Omit<ImageRecord, 'localBlob' | 'localThumbnail'>,
): boolean {
  // If timestamps differ, newer timestamp wins
  if (incoming.createdAt !== existing.createdAt) {
    return incoming.createdAt > existing.createdAt;
  }

  // If timestamps are equal, prefer synced over pending
  if (incoming.uploadStatus === 'synced' && existing.uploadStatus !== 'synced') {
    return true;
  }
  if (incoming.uploadStatus !== 'synced' && existing.uploadStatus === 'synced') {
    return false;
  }

  // If both have same status, use ID as tie-breaker (deterministic ordering)
  return incoming.id > existing.id;
}

/**
 * Merge remote images into local database
 * Note: Remote images don't have localBlob/localThumbnail, so we preserve local blobs when merging
 */
export async function mergeRemoteImages(
  incoming: Array<Omit<ImageRecord, 'localBlob' | 'localThumbnail'>>,
): Promise<MergeResult> {
  if (incoming.length === 0) return { applied: 0, replaced: 0 };

  // Validate all incoming images
  const validated = incoming.map((img) => {
    // Create a partial record for validation (without blobs)
    const partial = {
      ...img,
      localBlob: new Blob(), // Placeholder for validation
      localThumbnail: new Blob(), // Placeholder for validation
    };
    return ImageRecordSchema.parse(partial);
  });

  let applied = 0;
  let replaced = 0;

  await db.transaction('rw', db.images, async () => {
    for (const incomingImage of validated) {
      const existing = await db.images.get(incomingImage.id);
      if (existing) {
        // Conflict resolution: last-write-wins (createdAt + uploadStatus)
        if (shouldReplaceImage(existing, incomingImage)) {
          // Preserve local blobs if they exist and incoming doesn't have them
          const updated: ImageRecord = {
            ...incomingImage,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Blobs are runtime types, type-safe at compile time
            localBlob: existing.localBlob, // Keep local blob
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Blobs are runtime types, type-safe at compile time
            localThumbnail: existing.localThumbnail, // Keep local thumbnail
          };
          await db.images.put(updated);
          replaced += 1;
        }
        // Otherwise keep existing image
      } else {
        // New image - but we don't have local blobs, so create placeholder blobs
        // The user will need to download the actual images from storage URLs
        const newImage: ImageRecord = {
          ...incomingImage,
          localBlob: new Blob(), // Placeholder - will be loaded from remoteUrl if needed
          localThumbnail: new Blob(), // Placeholder - will be loaded from remoteThumbnailUrl if needed
        };
        await db.images.add(newImage);
        applied += 1;
      }
    }
  });

  return { applied, replaced };
}
